import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { ArrowUp, ArrowUpRight, Phone } from 'lucide-react'
import { gsap, MOTION_QUERY, useGSAP } from '@/lib/gsap'
import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { TransitionLink } from '@/components/generals/transition-link'
import { footerNav, siteConfig, QUOTE_PATH } from '@/constants/site'

export const Footer: FC = (): ReactNode => {
    const stageRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const stage = stageRef.current

        if (!stage) return

        const media = gsap.matchMedia()

        media.add(MOTION_QUERY, () => {
            const timeline = gsap.timeline({
                scrollTrigger: { trigger: stage, start: 'top bottom', end: 'bottom bottom', scrub: 0.6 }
            })

            timeline
                .fromTo('[data-footer-word]', { yPercent: 70 }, { yPercent: 0, ease: 'none' }, 0)
                .fromTo('[data-footer-line]', { scaleX: 0 }, { scaleX: 1, ease: 'none' }, 0)
        })

        return () => media.revert()
    }, { scope: stageRef })

    return (
        <footer className='relative overflow-hidden bg-ink-deep pb-28 text-cream md:pb-0'>
            <div className='bg-noise pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay' aria-hidden='true' />

            <Container className='relative'>
                <div className='grid gap-12 border-b border-cream/10 py-16 md:py-20 lg:grid-cols-12 lg:gap-10'>
                    <div className='lg:col-span-4'>
                        <Logo tone='light' />

                        <p className='mt-6 max-w-xs text-[15px] leading-relaxed text-cream/60'>
                            Fixing and fitting garage doors across the Riverton metro since {siteConfig.founded}, with a written price before every job.
                        </p>

                        <div className='mt-8 flex flex-wrap gap-3'>
                            <ButtonLink href={QUOTE_PATH} size='sm'>
                                Book a visit
                            </ButtonLink>

                            <a
                                href={siteConfig.phoneHref}
                                className='inline-flex h-10 items-center gap-2 rounded-full border border-cream/20 px-4 font-mono text-[13px] text-cream transition-colors hover:border-cream/50 hover:bg-cream/[0.06]'
                            >
                                <Phone className='size-3.5' aria-hidden='true' />
                                {siteConfig.phone}
                            </a>
                        </div>
                    </div>

                    <div className='grid grid-cols-2 gap-10 sm:grid-cols-4 lg:col-span-8'>
                        {footerNav.map(group => (
                            <div key={group.title}>
                                <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-cream/40'>
                                    {group.title}
                                </p>

                                <ul className='mt-5 space-y-3'>
                                    {group.links.map(link => (
                                        <li key={link.href}>
                                            <TransitionLink
                                                href={link.href}
                                                className='group inline-flex items-center gap-1 text-[14.5px] text-cream/75 transition-colors hover:text-cream'
                                            >
                                                {link.label}

                                                <ArrowUpRight
                                                    className='size-3.5 -translate-x-1 opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100'
                                                    aria-hidden='true'
                                                />
                                            </TransitionLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}

                        <div>
                            <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-cream/40'>
                                Find us
                            </p>

                            <address className='mt-5 space-y-3 text-[14.5px] not-italic leading-relaxed text-cream/75'>
                                <span className='block'>{siteConfig.address}</span>
                                <span className='block'>{siteConfig.hours}</span>
                                <span className='block'>{siteConfig.emergencyHours}</span>

                                <a href={siteConfig.phoneHref} className='block font-mono text-cream transition-colors hover:text-ember-soft'>
                                    {siteConfig.phone}
                                </a>
                            </address>
                        </div>
                    </div>
                </div>

                <div className='flex flex-col gap-4 py-7 text-[13px] text-cream/45 md:flex-row md:items-center md:justify-between'>
                    <p className='flex flex-wrap items-center gap-x-3 gap-y-1'>
                        <span className='inline-flex items-center gap-2 rounded-full bg-cream/[0.06] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-cream/75'>
                            <span className='relative flex size-1.5' aria-hidden='true'>
                                <span className='absolute inset-0 rounded-full bg-moss-soft animate-live' />
                                <span className='relative size-1.5 rounded-full bg-moss-soft' />
                            </span>
                            Line open until {siteConfig.emergencyUntil}
                        </span>
                        <span>{siteConfig.license} · {siteConfig.insured}</span>
                    </p>

                    <div className='flex items-center gap-5'>
                        <p>© 2026 {siteConfig.legalName}</p>

                        <button
                            type='button'
                            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                            className='group inline-flex items-center gap-2 text-cream/70 transition-colors hover:text-cream'
                        >
                            Back to top
                            <span className='flex size-8 items-center justify-center rounded-full border border-cream/20 transition-[background-color,border-color,translate] duration-500 ease-expo group-hover:-translate-y-0.5 group-hover:border-cream group-hover:bg-cream group-hover:text-ink'>
                                <ArrowUp className='size-3.5' aria-hidden='true' />
                            </span>
                        </button>
                    </div>
                </div>

                <div ref={stageRef} className='relative overflow-hidden' aria-hidden='true'>
                    <p
                        data-footer-word
                        className='footer-wordmark pointer-events-none -mb-[0.16em] select-none text-center font-heading text-[22vw] font-bold leading-[0.8] tracking-[0.01em] xl:text-[19rem]'
                    >
                        Liftline
                    </p>

                    <div className='absolute inset-x-0 bottom-0 h-px bg-cream/10' />
                    <div data-footer-line className='absolute bottom-0 left-0 h-[3px] w-full origin-left bg-ember-soft' />
                </div>
            </Container>
        </footer>
    )
}
